/**
 * Variable Profit Analysis Card for Accountant Report View
 * Displays the 4-step VP calculation breakdown with methodology
 */

import React from 'react';
import CalculationMethodology from '../CalculationMethodology';

interface VariableProfitAnalysis {
  variable_margin_pct?: number;
  benchmark_vp?: number;
  coverage_vp?: number;
  vp_loss?: number;
}

interface BenchmarkPeriod {
  revenue?: number;
  variable_profit?: number;
}

interface VariableProfitAnalysisCardProps {
  variableProfitAnalysis: VariableProfitAnalysis;
  benchmarkPeriod: BenchmarkPeriod;
  formatCurrency: (amount?: number) => string;
  formatPercent: (value?: number) => string;
}

export default function VariableProfitAnalysisCard({
  variableProfitAnalysis,
  benchmarkPeriod,
  formatCurrency,
  formatPercent
}: VariableProfitAnalysisCardProps) {
  if (!variableProfitAnalysis) return null;

  // Calculate coverage revenue from VP and margin
  const calculateCoverageRevenue = () => {
    if (variableProfitAnalysis.coverage_vp && variableProfitAnalysis.variable_margin_pct) {
      return variableProfitAnalysis.coverage_vp / (variableProfitAnalysis.variable_margin_pct / 100);
    }
    return undefined;
  };

  const coverageRevenue = calculateCoverageRevenue();

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
        <span className="text-2xl mr-2">💰</span>
        Variable Profit Analysis
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Step 1: Variable Margin */}
        <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-600">
          <p className="text-xs text-blue-700 font-medium mb-1">STEP 1</p>
          <p className="text-sm text-blue-800 mb-2 font-semibold">Variable Margin</p>
          <p className="text-2xl font-bold text-blue-900 mb-2">
            {formatPercent(variableProfitAnalysis.variable_margin_pct)}
          </p>
          <div className="text-xs text-blue-700 bg-blue-100 rounded px-2 py-1 font-mono">
            {formatCurrency(benchmarkPeriod?.variable_profit)} ÷ {formatCurrency(benchmarkPeriod?.revenue)}
          </div>
        </div>

        {/* Step 2: Benchmark VP */}
        <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-gray-400">
          <p className="text-xs text-gray-600 font-medium mb-1">STEP 2</p>
          <p className="text-sm text-gray-700 mb-2 font-semibold">Benchmark VP</p>
          <p className="text-2xl font-bold text-gray-900 mb-2">
            {formatCurrency(variableProfitAnalysis.benchmark_vp)}
          </p>
          <div className="text-xs text-gray-700 bg-gray-200 rounded px-2 py-1 font-mono">
            {formatCurrency(benchmarkPeriod?.revenue)} × {formatPercent(variableProfitAnalysis.variable_margin_pct)}
          </div>
        </div>

        {/* Step 3: Coverage VP */}
        <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-gray-400">
          <p className="text-xs text-gray-600 font-medium mb-1">STEP 3</p>
          <p className="text-sm text-gray-700 mb-2 font-semibold">Coverage VP</p>
          <p className="text-2xl font-bold text-gray-900 mb-2">
            {formatCurrency(variableProfitAnalysis.coverage_vp)}
          </p>
          <div className="text-xs text-gray-700 bg-gray-200 rounded px-2 py-1 font-mono">
            {coverageRevenue ? `${formatCurrency(coverageRevenue)} × ${formatPercent(variableProfitAnalysis.variable_margin_pct)}` : 'N/A'}
          </div>
        </div>

        {/* Step 4: VP Loss */}
        <div className="bg-red-50 rounded-lg p-4 border-l-4 border-red-600">
          <p className="text-xs text-red-700 font-medium mb-1">STEP 4: RESULT</p>
          <p className="text-sm text-red-800 mb-2 font-semibold">VP Loss</p>
          <p className="text-2xl font-bold text-red-900 mb-2">
            {formatCurrency(variableProfitAnalysis.vp_loss)}
          </p>
          <div className="text-xs text-red-700 bg-red-100 rounded px-2 py-1 font-mono">
            {formatCurrency(variableProfitAnalysis.benchmark_vp)} − {formatCurrency(variableProfitAnalysis.coverage_vp)}
          </div>
        </div>
      </div>
      
      {/* Methodology Explanation */}
      <CalculationMethodology 
        section="variable_profit" 
        data={{
          ...variableProfitAnalysis,
          benchmark_revenue: benchmarkPeriod?.revenue,
          benchmark_variable_expenses: benchmarkPeriod?.revenue && benchmarkPeriod?.variable_profit 
            ? benchmarkPeriod.revenue - benchmarkPeriod.variable_profit 
            : undefined,
          coverage_revenue: coverageRevenue,
          coverage_variable_expenses: coverageRevenue && variableProfitAnalysis.coverage_vp
            ? coverageRevenue - variableProfitAnalysis.coverage_vp
            : undefined
        }} 
      />
    </div>
  );
}
